import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { CountryCode } from '@sysarb/sysarb-types/general/enums';
import type { EmploymentAdjustedMeanStatistics, EmploymentAdjustedMedianStatistics, EmploymentPayGapPerGrade, EmploymentPayQuartile, SalaryComponentPercentageByGender } from '@sysarb/sysarb-types-v2/services/v1/base-registry';
import type { QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';

const baseRegistryService = new RestManager(`https://${getApiHost()}/base-registry-service`);

export type StatisticsFilters = {
    countryCode?: CountryCode;
    legalEntityId?: string;
};

type StatisticsOptions = {
    queries: {
        getEmploymentPayQuartiles: (filters?: StatisticsFilters) => QueryOptions<EmploymentPayQuartile | null, Error>;
        getEmploymentUnadjustedMeanStatistics: (filters?: StatisticsFilters) => QueryOptions<EmploymentAdjustedMeanStatistics | null, Error>;
        getEmploymentUnadjustedMedianStatistics: (filters?: StatisticsFilters) => QueryOptions<EmploymentAdjustedMedianStatistics | null, Error>;
        getEmploymentSalaryComponentsPercentageStatistics: (filters?: StatisticsFilters) => QueryOptions<SalaryComponentPercentageByGender | null, Error>;
        getEmploymentPayGapByGrade: (filters?: StatisticsFilters) => QueryOptions<EmploymentPayGapPerGrade[] | null, Error>;
    };
};

function buildSearchQuery(filters: StatisticsFilters): string {
    const searchParams = new URLSearchParams();
    if (filters.countryCode != null) searchParams.set('countryCode', filters.countryCode);
    if (filters.legalEntityId != null) searchParams.set('legalEntityId', filters.legalEntityId);
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
}

export const statistics: StatisticsOptions = {
    queries: {
        getEmploymentPayQuartiles: (filters: StatisticsFilters = {}): QueryOptions<EmploymentPayQuartile | null, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<EmploymentPayQuartile | null, Error>({
                queryKey: ['section-manager', 'employment-pay-quartiles', instance, filters],
                queryFn: async (): Promise<EmploymentPayQuartile | null> => {
                    const url = `v2/${instance}/base-register/employments/pay-quartiles${buildSearchQuery(filters)}`;
                    return baseRegistryService.get<EmploymentPayQuartile>(url);
                },
            });
        },
        getEmploymentUnadjustedMeanStatistics: (filters: StatisticsFilters = {}): QueryOptions<EmploymentAdjustedMeanStatistics | null, Error> => {
            const { instance } = useLoginStore();
            const version = 'v2';

            return queryOptions<EmploymentAdjustedMeanStatistics | null, Error>({
                queryKey: ['section-manager', 'employment-unadjusted-mean-statistics', instance, version, filters],
                queryFn: async (): Promise<EmploymentAdjustedMeanStatistics | null> => {
                    const url = `${version}/${instance}/base-register/employments/mean-unadjusted-statistics${buildSearchQuery(filters)}`;
                    return baseRegistryService.get<EmploymentAdjustedMeanStatistics>(url);
                },
            });
        },
        getEmploymentUnadjustedMedianStatistics: (filters: StatisticsFilters = {}): QueryOptions<EmploymentAdjustedMedianStatistics | null, Error> => {
            const { instance } = useLoginStore();
            const version = 'v2';

            return queryOptions<EmploymentAdjustedMedianStatistics | null, Error>({
                queryKey: ['section-manager', 'employment-unadjusted-median-statistics', instance, version, filters],
                queryFn: async (): Promise<EmploymentAdjustedMedianStatistics | null> => {
                    const url = `${version}/${instance}/base-register/employments/median-unadjusted-statistics${buildSearchQuery(filters)}`;
                    return baseRegistryService.get<EmploymentAdjustedMedianStatistics>(url);
                },
            });
        },
        getEmploymentSalaryComponentsPercentageStatistics: (filters: StatisticsFilters = {}): QueryOptions<SalaryComponentPercentageByGender | null, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<SalaryComponentPercentageByGender | null, Error>({
                queryKey: ['section-manager', 'employment-salary-components-percentage-statistics', instance, filters],
                queryFn: async (): Promise<SalaryComponentPercentageByGender | null> => {
                    const url = `v2/${instance}/base-register/employments/salary-components-percentage-statistics${buildSearchQuery(filters)}`;
                    return baseRegistryService.get<SalaryComponentPercentageByGender>(url);
                },
            });
        },
        getEmploymentPayGapByGrade: (filters: StatisticsFilters = {}): QueryOptions<EmploymentPayGapPerGrade[] | null, Error> => {
            const { instance, currentOrganisationNodeId } = useLoginStore();
            const version = 'v2';

            return queryOptions<EmploymentPayGapPerGrade[] | null, Error>({
                queryKey: ['section-manager', 'employment-pay-gap-by-grade', instance, currentOrganisationNodeId, version, filters],
                queryFn: async (): Promise<EmploymentPayGapPerGrade[] | null> => {
                    const url = `${version}/${instance}/${currentOrganisationNodeId}/base-register/section-manager/employments/pay-gap-by-grade${buildSearchQuery(filters)}`;
                    return baseRegistryService.get<EmploymentPayGapPerGrade[]>(url);
                },
                enabled: () => currentOrganisationNodeId != null,
            });
        },
    },
};
